import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, Chip, TextField, Typography, Modal, Box } from '@mui/material'
import { Props, RefFilterSelected } from './props'
import { useState, SyntheticEvent, useEffect, useImperativeHandle, forwardRef, Ref } from 'react'
import { Item } from '../../../utils/types/generals'
import { useTranslation } from 'react-i18next'
import { CustomPaper } from '../../CustomPaper'
import { ChipsList } from '../../chipsList'
import { useContextState } from '../../../hooks/theme'

const styles = {
  caption: {
    width: '100%',
    wordBreak: 'break-all',
  },
  chip: {
    display: 'none',
    width: '90%',
    height: 'fit-content',
    wordBreak: 'break-all',
    background: '#00000011',
    '& .MuiChip-label': {
      textOverflow: 'clip',
      whiteSpace: 'break-spaces',
    },
  },
  input: {
    '& .MuiOutlinedInput-root': {
      height: '53px',
      display: 'flex',
      flexDirection: 'column',
    },
    '& .MuiAutocomplete-tag': {
      maxWidth: 'calc(100% - 22px)',
      display: 'none',
    },
  },
  chipUnFocus: {
    background: '#31c462',
    color: 'white',

    '&.MuiChip-clickable:hover': {
      backgroundColor: '#31c462',
    },
  },
}
const FilterSelect = forwardRef(({ hidechips, showAllInModal, ...props }: Props & { showAllInModal?: boolean }, ref: Ref<RefFilterSelected>) => {
  const [value, setValue] = useState<Item[]>([])
  const { t } = useTranslation()
  const [isFocus, setIsFocus] = useState(false)
  const [openModal, setOpenModal] = useState(false);
  const { colors } = useContextState()

  useImperativeHandle(ref, () => ({
    deleteChips: handleDelete
  }))

  const handleChange = (_: SyntheticEvent<Element, Event>, value: any[]) => {
    props.onChange({
      target: {
        name: props.name,
        value: value,
      },
    })
  }
  const filterItems = () => {
    let newList: Item[] = []
    props.items?.forEach((item: any) => {
      if (!value?.find((itemValue: any) => itemValue?.value === item?.value)) {
        newList = [...newList, item]
      }
    })

    return newList
  }

  const handleDelete = (option: any) => {
    let values = [...value]
    values = values.filter((e) => e.value != option.value)

    props.onChange({
      target: {
        name: props.name,
        value: values,
      },
    })
  }
  useEffect(() => {
    setValue(props.items?.filter((item) => props.selected?.some((itemSelect: Item) => itemSelect.value == item.value)) ?? [])
    // eslint-disable-next-line
  }, [props.selected])

  return (
    <>
      <Autocomplete
        {...props}
        key={props.name}
        sx={{ ...props.sx, ...(!isFocus ? styles.input : {}) }}
        noOptionsText={t('general.noOptions')}
        multiple
        options={filterItems() ?? []}
        getOptionLabel={(option) => option.label ?? ''}
        value={value}
        limitTags={1}
        PaperComponent={CustomPaper}
        disableCloseOnSelect
        autoComplete={false}
        filterSelectedOptions
        disabled={props.disabled}
        onFocus={() => {
          setIsFocus(true)
        }}
        onBlur={() => {
          setIsFocus(false)
        }}
        onChange={handleChange}
        renderTags={(value, getTagProps) => {
          return value.map((option, index) => <Chip sx={isFocus ? styles.chip : styles.chipUnFocus} variant="outlined" label={option.label} {...getTagProps({ index })} />)
        }}
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.value}>
              {
                !!option?.children ? option.children : <Typography variant='simple_text'>{option.label}</Typography>
              }
            </li>
          )
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            disabled={props.disabled}
            name={props.name}
            label={props.label}
            helperText={!!props?.disabled ? false : (props?.errors?.[props?.name]?.translate ? t(props?.errors?.[props?.name]?.translate) : props?.errors?.[props?.name]) ?? ''}
            error={!!props?.disabled ? false : props?.errors?.[props?.name]?.translate ?? props?.errors?.[props?.name]}
          />
        )}
      />

      {!hidechips && <ChipsList value={value} handleDelete={handleDelete} showAllInModal={showAllInModal} onShowModal={() => setOpenModal(true)} label={props.label} />}
      {showAllInModal && (
        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <Box sx={{
            background: colors.color5,
            p: '12px',
            borderRadius: 2,
            width: 350,
            maxWidth: 500,
            maxHeight: '80vh',
            overflowY: 'auto',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            outline: 'none',
          }}>
            <Typography variant="h6" sx={{ mb: 2, mt: 1, fontWeight: 'bold', color: colors.color13, fontSize: '16px' }}>
              {props.label} <span> seleccionadas</span>
            </Typography>
            <Box
              sx={{
                height: '350px',
                maxHeight: '350px',
                overflowY: 'auto',
                mb: 2,
              }}
            >

              {props.name === 'zones' && Array.isArray(props.items) && props.items.length > 0 && (
                value.map((zone, idx) => (
                  <Box key={zone.value || idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e0e0e0', borderRadius: 1, mb: '8px', px: '4px', py: '2px' }}>
                    <Typography sx={{ fontSize: '12px', color: colors.color13 }}>{zone.label}</Typography>
                    <Box component="span" sx={{ cursor: 'pointer', color: colors.color13, fontWeight: 'bold', ml: 1, display: 'flex', alignItems: 'center' }} onClick={() => handleDelete(zone)} aria-label="delete">
                      <CloseIcon fontSize="small" />
                    </Box>
                  </Box>
                ))
              )}
              {props.name === 'units' && Array.isArray(props.items) && props.items.length > 0 && (
                value.map((unit, idx) => (
                  <Box key={unit.value || idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e0e0e0', borderRadius: 1, mb: '8px', px: '4px', py: '2px' }}>
                    <Typography sx={{ fontSize: '12px', color: colors.color13 }}>{unit.label}</Typography>
                    <Box component="span" sx={{ cursor: 'pointer', color: colors.color13, fontWeight: 'bold', ml: 1, display: 'flex', alignItems: 'center' }} onClick={() => handleDelete(unit)} aria-label="delete">
                      <CloseIcon fontSize="small" />
                    </Box>
                  </Box>
                ))
              )}
              {props.name !== 'zones' && props.name !== 'units' &&
                Array.from(
                  value.reduce((map, item) => {
                    if (!map.has(item.value)) map.set(item.value, item);
                    return map;
                  }, new Map())
                .values()
                ).map((option, idx) => (
                  <Box key={option.value} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e0e0e0', borderRadius: 1, mb: '12px', px: '4px', py: '2px' }}>
                    <Typography sx={{ fontSize: '12px', color: colors.color13 }}>{option.label}</Typography>
                    <Box component="span" sx={{ cursor: 'pointer', color: colors.color13, fontWeight: 'bold', ml: 1, display: 'flex', alignItems: 'center' }} onClick={() => handleDelete(option)} aria-label="delete">
                      <CloseIcon fontSize="small" />
                    </Box>
                  </Box>
                ))
              }
              {props.name === 'units' && Array.isArray(props.items) && props.items.length > 0 && (
                (() => {
                  const allZones = props.allZones || [];
                  const zonesFromUnits = Array.from(new Set(props.items.map((u) => u.zoneId)));
                  const selectedUnits = value;
                  return allZones.length > 0 ?
                    allZones.map((zone) => {
                      const unitsOfZone = selectedUnits.filter((u) => u.zoneId === zone.value);
                      if (!unitsOfZone.length) return null;
                      return (
                        <Box key={zone.value} sx={{ mb: 1 }}>
                          <Typography sx={{ fontWeight: 'bold', color: colors.color13, fontSize: '13px', mb: 0.5 }}>{zone.label}</Typography>
                          {unitsOfZone.map((unit) => (
                            <Typography key={unit.value} style={{ padding: '2px 4px', border: '1px solid #e0e0e0', marginBottom: '8px', fontSize: '12px', borderRadius: 4, color: colors.color13 }}>
                              {unit.label}
                            </Typography>
                          ))}
                        </Box>
                      );
                    })
                  :
                    zonesFromUnits.map((zoneId) => {
                      const unitsOfZone = selectedUnits.filter((u) => u.zoneId === zoneId);
                      if (!unitsOfZone.length) return null;
                      return (
                        <Box key={zoneId} sx={{ mb: 1 }}>
                          <Typography sx={{ fontWeight: 'bold', color: colors.color13, fontSize: '13px', mb: 0.5 }}>{`Zona ${zoneId}`}</Typography>
                          {unitsOfZone.map((unit) => (
                            <Typography key={unit.value} style={{ padding: '2px 4px', border: '1px solid #e0e0e0', marginBottom: '8px', fontSize: '12px', borderRadius: 4, color: colors.color13 }}>
                              {unit.label}
                            </Typography>
                          ))}
                        </Box>
                      );
                    });
                })()
              )}
            </Box>
            <Box style={{ marginTop: 2, textAlign: 'right' }}>
              <button style={{ padding: '6px 16px', borderRadius: 4, border: 'none', background: '#1976d2', color: 'white', fontWeight: 600, cursor: 'pointer' }} onClick={() => setOpenModal(false)}>Aceptar</button>
            </Box>
          </Box>
        </Modal>
      )}
    </>
  )
})

export default FilterSelect
